import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { FiArrowLeft, FiCheck, FiClock, FiAlertTriangle, FiFileText, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import config from '../config';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #0F172A;
  color: #F8FAFC;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;

  h1 {
    font-size: 1.5rem;
    font-weight: 500;
    color: #F8FAFC;
  }

  button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    color: #94A3B8;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 0.375rem;
    transition: all 0.2s;

    &:hover {
      background: #1E293B;
      color: #F8FAFC;
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
`;

const Card = styled.div`
  background: #1E293B;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
`;

const ProcessingSteps = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  position: relative;
  padding: 0 1rem;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background: #334155;
    z-index: 0;
  }
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  flex: 1;

  .icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: ${props => props.status === 'completed' ? '#059669' : 
                         props.status === 'current' ? '#2563EB' :
                         props.status === 'error' ? '#DC2626' : '#334155'};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    margin-bottom: 0.5rem;
    transition: all 0.3s;
  }

  .label {
    font-size: 0.875rem;
    color: ${props => props.status === 'completed' ? '#34D399' :
                     props.status === 'current' ? '#60A5FA' :
                     props.status === 'error' ? '#F87171' : '#94A3B8'};
    text-align: center;
  }

  .time {
    font-size: 0.75rem;
    color: #64748B;
    margin-top: 0.25rem;
  }
`;

const AgentCard = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: ${props => props.active ? '#334155' : '#1E293B'};
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  transition: all 0.2s;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};

  &:hover {
    background: ${props => props.clickable ? '#475569' : '#1E293B'};
  }

  .icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: ${props => props.status === 'completed' ? '#059669' :
                         props.status === 'current' ? '#2563EB' :
                         props.status === 'error' ? '#DC2626' : '#334155'};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }

  .content {
    flex: 1;

    h3 {
      font-size: 0.875rem;
      font-weight: 500;
      color: #F8FAFC;
      margin-bottom: 0.25rem;
    }

    p {
      font-size: 0.75rem;
      color: #94A3B8;
    }
  }

  .status {
    font-size: 0.75rem;
    color: ${props => props.status === 'completed' ? '#34D399' :
                     props.status === 'current' ? '#60A5FA' :
                     props.status === 'error' ? '#F87171' : '#94A3B8'};
  }
`;

const MLCard = styled(Card)`
  .confidence {
    text-align: center;
    margin: 2rem 0;

    h3 {
      font-size: 2.5rem;
      font-weight: 600;
      color: #34D399;
      margin-bottom: 0.5rem;
    }

    p {
      color: #94A3B8;
      font-size: 0.875rem;
    }
  }
`;

const ResultsTable = styled.div`
  margin-top: 2rem;

  .section {
    margin-bottom: 2rem;

    h3 {
      font-size: 1rem;
      font-weight: 500;
      color: #94A3B8;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid #334155;
    }
  }

  table {
    width: 100%;
    border-collapse: collapse;

    th, td {
      padding: 0.75rem;
      text-align: left;
      border-bottom: 1px solid #334155;
      font-size: 0.875rem;
    }

    th {
      color: #94A3B8;
      font-weight: 500;
    }

    td {
      color: #F8FAFC;
    }

    .confidence {
      color: #34D399;
    }
  }
`;

const DocumentDetails = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch document details from the populate-ui endpoint
        const docResponse = await axios.get(`${config.API_BASE_URL}/populate-ui`);
        const documentDetails = docResponse.data.find(doc => doc.document_id === documentId);
        setDocument(documentDetails);

        // Fetch processing results
        const resultsResponse = await axios.get(`${config.API_BASE_URL}/api/v1/get-results/${documentId}`);
        setResults(resultsResponse.data);
      } catch (error) {
        console.error('Error fetching document details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [documentId]);

  const getStepStatus = (step) => {
    if (!document) return 'pending';
    const status = document.status?.toLowerCase();
    
    switch (step) {
      case 'preprocessing':
        return status === 'pending' ? 'current' : 'completed';
      case 'chunking':
        return status === 'pending' ? 'pending' : 
               status === 'processing' ? 'current' : 'completed';
      case 'processing':
        return status === 'pending' || status === 'processing' ? 'pending' :
               status === 'processed' ? 'current' : 'completed';
      case 'finalization':
        return status === 'completed' ? 'completed' : 'pending';
      default:
        return 'pending';
    }
  };

  const renderAgents = () => {
    const agents = [
      {
        name: 'Document Preprocessing Agent',
        description: 'Conducts rigorous S3 document retrieval with cryptographic integrity checks',
        status: getStepStatus('preprocessing')
      },
      {
        name: 'Document Chunking Agent',
        description: 'Splits document into processable chunks',
        status: getStepStatus('chunking')
      },
      {
        name: 'LLM Processing Agent',
        description: 'Processes document chunks using advanced language models',
        status: getStepStatus('processing')
      },
      {
        name: 'Response Finalization Agent',
        description: 'Combines results and performs final analysis',
        status: getStepStatus('finalization')
      }
    ];

    return agents.map((agent, index) => (
      <AgentCard 
        key={index}
        status={agent.status}
        active={agent.status === 'current'}
        clickable={agent.status === 'pending' && agent.name === 'Document Preprocessing Agent'}
      >
        <div className="icon">
          {agent.status === 'completed' ? <FiCheck /> :
           agent.status === 'current' ? <FiClock /> :
           agent.status === 'error' ? <FiAlertTriangle /> : <FiFileText />}
        </div>
        <div className="content">
          <h3>{agent.name}</h3>
          <p>{agent.description}</p>
        </div>
        <div className="status">
          {agent.status.charAt(0).toUpperCase() + agent.status.slice(1)}
        </div>
      </AgentCard>
    ));
  };

  const renderResults = () => {
    if (!results?.merged_content) return null;

    const { shares, subfunds, ...otherDetails } = results.merged_content;

    return (
      <ResultsTable>
        {subfunds && subfunds.length > 0 && (
          <div className="section">
            <h3>Subfunds</h3>
            <table>
              <thead>
                <tr>
                  <th>Fund Name</th>
                  <th>Benchmark</th>
                  <th>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {subfunds.map((subfund, index) => (
                  <tr key={index}>
                    <td>{subfund.fundName.value}</td>
                    <td>{subfund.benchmarkfund.value}</td>
                    <td className="confidence">{(subfund.fundName.confidence * 100).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {shares && shares.length > 0 && (
          <div className="section">
            <h3>Share Classes</h3>
            <table>
              <thead>
                <tr>
                  <th>Share Class</th>
                  <th>Redemption Fee</th>
                  <th>Management Fee</th>
                  <th>Min Investment</th>
                </tr>
              </thead>
              <tbody>
                {shares.map((share, index) => (
                  <tr key={index}>
                    <td>{share.shareClass.value}</td>
                    <td>{share['Redemption Fee'].value}</td>
                    <td>{share['Max Management fee'].value}</td>
                    <td>{share['Minimum initial Investment amount with purchase currency'].value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="section">
          <h3>Other Details</h3>
          <table>
            <tbody>
              {Object.entries(otherDetails).map(([key, value]) => (
                <tr key={key}>
                  <td>{key.replace(/([A-Z])/g, ' $1').trim()}</td>
                  <td>{value.value}</td>
                  <td className="confidence">{(value.confidence * 100).toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ResultsTable>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <Header>
          <button onClick={() => navigate(-1)}>
            <FiArrowLeft />
            Back
          </button>
          <h1>Loading...</h1>
        </Header>
      </PageContainer>
    );
  }

  if (!document) {
    return (
      <PageContainer>
        <Header>
          <button onClick={() => navigate(-1)}>
            <FiArrowLeft />
            Back
          </button>
          <h1>Document not found</h1>
        </Header>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <button onClick={() => navigate(-1)}>
          <FiArrowLeft />
          Back
        </button>
        <h1>{document?.document_name || 'Document Details'}</h1>
      </Header>

      <ContentGrid>
        <div>
          <Card>
            <ProcessingSteps>
              {['preprocessing', 'chunking', 'processing', 'finalization'].map((step, index) => (
                <Step key={step} status={getStepStatus(step)}>
                  <div className="icon">
                    {getStepStatus(step) === 'completed' ? <FiCheckCircle /> :
                     getStepStatus(step) === 'current' ? <FiClock /> :
                     getStepStatus(step) === 'error' ? <FiAlertCircle /> : <FiClock />}
                  </div>
                  <div className="label">
                    {step.charAt(0).toUpperCase() + step.slice(1)}
                  </div>
                  {document?.processing_times?.[step] && (
                    <div className="time">{document.processing_times[step]}s</div>
                  )}
                </Step>
              ))}
            </ProcessingSteps>

            {renderAgents()}
          </Card>

          <Card style={{ marginTop: '1.5rem' }}>
            {renderResults()}
          </Card>
        </div>

        <MLCard>
          <h2>ML Inference</h2>
          <div className="confidence">
            <h3>{document?.ml_inference_confidence || '85'}%</h3>
            <p>Confidence Score</p>
          </div>

          <div style={{ color: '#94A3B8', fontSize: '0.875rem', lineHeight: '1.5' }}>
            <p>The ML model has processed this document with high confidence. The extracted information has been verified against our training data and shows strong consistency with expected patterns.</p>
          </div>
        </MLCard>
      </ContentGrid>
    </PageContainer>
  );
};

export default DocumentDetails; 